import React, { useState, useEffect, useCallback } from "react";
import {
  PenLine, FileCheck, Shield, Clock, CheckCircle2,
  XCircle, AlertTriangle, Search
} from "lucide-react";
import SignatureDialog from "../components/SignatureDialog";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ESignaturesPage() {
  const [mySigs, setMySigs] = useState([]);
  const [verifyId, setVerifyId] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  // PR_RUN_041 — inline sign form replaced by the SignatureDialog modal
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [recentSignatureId, setRecentSignatureId] = useState(null);

  const fetchData = useCallback(async () => {
    const opts = { credentials: "include" };
    const s = await fetch(`${API}/api/esignatures/my-signatures`, opts)
      .then(r => r.ok ? r.json() : { signatures: [] });
    setMySigs(s.signatures || []);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const verify = async () => {
    if (!verifyId) return;
    const res = await fetch(`${API}/api/esignatures/verify/${verifyId}`);
    if (res.ok) setVerifyResult(await res.json());
    else setVerifyResult({ error: "Not found" });
  };

  return (
    <div className="min-h-screen bg-[#0D0F14] text-white p-6 lg:p-10" data-testid="esignatures-page">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-3">
        <PenLine size={24} className="text-[#C9A84C]" /> CFR Part 11 — Electronic Signatures
      </h1>

      {/* Sign Document — PR_RUN_041: launcher → SignatureDialog */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.06)] p-4 mb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[rgba(201,168,76,0.12)] flex items-center justify-center flex-shrink-0">
              <FileCheck size={16} className="text-[#C9A84C]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Apply a signature</h3>
              <p className="text-[11px] text-[#8B95A5] mt-0.5">
                Re-authenticated CFR Part 11 §11.50/§11.70/§11.200 signature with frozen manifestation + audit-row hash.
              </p>
            </div>
          </div>
          <button
            onClick={() => setSignatureDialogOpen(true)}
            className="px-4 py-2 bg-[#C9A84C] text-[#0D0F14] rounded text-sm font-semibold hover:bg-[#B8973B] transition-colors flex items-center gap-1.5"
            data-testid="open-signature-dialog-btn"
          >
            <PenLine size={14} /> Open signature dialog
          </button>
        </div>
        {recentSignatureId && (
          <p className="text-xs text-[#4ADE80] mt-3 flex items-center gap-1.5" data-testid="sign-message">
            <CheckCircle2 className="w-3 h-3" /> Last signature: <span className="font-mono">{recentSignatureId}</span>
          </p>
        )}
      </div>

      <SignatureDialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSigned={(sig) => {
          setRecentSignatureId(sig.signature_id);
          fetchData();
        }}
      />

      {/* Verify Signature */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.06)] p-4 mb-6">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Search size={16} /> Verify Signature</h3>
        <div className="flex gap-2 mb-3">
          <input value={verifyId} onChange={e => setVerifyId(e.target.value)} placeholder="Signature ID (SIG-...)"
            className="flex-1 bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white"
            data-testid="verify-sig-id" />
          <button onClick={verify} className="px-4 py-2 bg-[#36A9E1] text-white rounded text-sm font-semibold"
            data-testid="verify-btn">Verify</button>
        </div>
        {verifyResult && !verifyResult.error && (
          <div className="bg-[rgba(74,222,128,0.05)] rounded p-3 text-xs" data-testid="verify-result">
            <div className="flex items-center gap-2 mb-1">
              {verifyResult.integrity_valid ? <CheckCircle2 size={14} className="text-[#4ADE80]" /> : <XCircle size={14} className="text-[#F87171]" />}
              <span className="font-semibold text-white">Integrity: {verifyResult.integrity_valid ? "VALID" : "INVALID"}</span>
            </div>
            <p className="text-[#8B95A5]">Signer: {verifyResult.signer_name} | Meaning: {verifyResult.meaning} | Date: {new Date(verifyResult.signed_at).toLocaleString()}</p>
          </div>
        )}
        {verifyResult?.error && <p className="text-xs text-[#F87171]">{verifyResult.error}</p>}
      </div>

      {/* My Signatures */}
      <div className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden">
        <div className="bg-[rgba(255,255,255,0.03)] px-4 py-2">
          <span className="text-xs text-[#8B95A5] uppercase tracking-wider">My Signatures</span>
        </div>
        {mySigs.length > 0 ? (
          <table className="w-full" data-testid="my-signatures-table">
            <thead><tr className="bg-[rgba(255,255,255,0.02)]">
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Date</th>
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Signature ID</th>
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Document</th>
              <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Meaning</th>
              <th className="text-center py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Status</th>
            </tr></thead>
            <tbody>
              {mySigs.map(s => (
                <tr key={s.signature_id} className="border-b border-[rgba(255,255,255,0.04)]">
                  <td className="py-2 px-3 text-[11px] text-[#8B95A5]">{new Date(s.signed_at).toLocaleDateString()}</td>
                  <td className="py-2 px-3 text-[11px] font-mono text-[#C9A84C]">{s.signature_id}</td>
                  <td className="py-2 px-3 text-[11px] text-white">{s.document_title || s.document_id}</td>
                  <td className="py-2 px-3 text-[11px] text-[#8B95A5]">{s.meaning?.split(" — ")[0]}</td>
                  <td className="py-2 px-3 text-center">
                    {s.revoked ? <span className="text-[#F87171] text-[10px]">REVOKED</span> : <CheckCircle2 size={12} className="text-[#4ADE80] inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="text-center py-8 text-[#555] text-sm" data-testid="no-signatures">No signatures yet</p>}
      </div>
    </div>
  );
}
